import styled, { css } from 'styled-components'
import { Tooltip } from '@mui/material'
import {
  HelpOutline,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
} from '@mui/icons-material'
import { getHHmm, getmm } from '../../utils/dateConversion'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 260px;
  width: 260px;
  height: 140px;
  padding: 12px;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  background-color: #fff;

  ${({ blocked }) =>
    blocked &&
    css`
      > span {
        color: transparent;
        text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
      }

      > div {
        color: transparent;
        text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
      }
    `}
`

const Head = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const Title = styled.span`
  font-size: 14px;
  color: #757575;
`

const Value = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: #202224;
`

const ComparisonContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #27292e;
  gap: 2px;
`

const ComparisonValue = styled.span`
  color: ${({ color }) => color};
  font-size: 14px;
  font-weight: 600;
`

const SignContainer = styled.div`
  position: absolute;
  top: 26px;
  background-color: #fbddd0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const TYPES = {
  number: value => value,
  milligram: value => `${value}mg`,
  percent: value => `${value}%`,
  hour: value => {
    const { hour, minutes } = value

    if (hour <= 0) return getmm({ minutes: minutes })

    return getHHmm({ hours: hour, minutes: minutes })
  },
}

const TREND = {
  positive: (
    <TrendingUp
      color='success'
      width={16}
      height={16}
    />
  ),
  neutral: (
    <TrendingFlat
      width={16}
      height={16}
    />
  ),
  negative: (
    <TrendingDown
      color='error'
      width={16}
      height={16}
    />
  ),
}

const getComparisonTrendColor = ({ value, trendStyle, comparisonType }) => {
  if (comparisonType === 'hour') {
    if (value.hour === 0 && value.minutes === 0) return

    return trendStyle === 'regular' ? '#00B69B' : '#BE3756'
  }

  if (value > 0) return trendStyle === 'regular' ? '#00B69B' : '#BE3756'
  if (value === 0) return
  return trendStyle === 'inverted' ? '#BE3756' : '#00B69B'
}

const getComparisonTrend = ({ value, trendStyle, comparisonType }) => {
  if (comparisonType === 'hour') {
    if (value.hour === 0 && value.minutes === 0) return 'neutral'

    return trendStyle === 'regular' ? 'positive' : 'negative'
  }

  if (value > 0) return trendStyle === 'regular' ? 'positive' : 'negative'
  if (value === 0) return 'neutral'
  return trendStyle === 'inverted' ? 'negative' : 'positive'
}

const BigNumber = ({
  title,
  value,
  type,
  comparisonValue,
  comparisonDescription,
  comparisonType,
  trendStyle,
  tooltip,
}) => {
  const { t } = useTranslation('common')
  if (!!!title) {
    return (
      <div style={{ position: 'relative' }}>
        <Container blocked>
          <Title>{t('contact_ialert')}</Title>
          <Value>-</Value>
          <ComparisonContainer>
            <span>-</span>
            <ComparisonValue>-</ComparisonValue>
            <span>-</span>
          </ComparisonContainer>
        </Container>
        <SignContainer>
          <span
            style={{
              padding: 4,
              textAlign: 'center',
              color: '#27292E',
              fontSize: 14,
            }}
          >
            {t('upsell_contact_full')}
          </span>
        </SignContainer>
      </div>
    )
  }

  const comparisonTrend = getComparisonTrend({
    value: comparisonValue,
    trendStyle,
    comparisonType,
  })

  return (
    <Container>
      <Head>
        <Title>{title}</Title>
        {tooltip && (
          <Tooltip
            title={tooltip}
            placement='right'
            arrow
            sx={{ color: '#757575' }}
          >
            <HelpOutline fontSize='18' />
          </Tooltip>
        )}
      </Head>
      <Value>{TYPES[type](value)}</Value>
      <ComparisonContainer>
        <span>{TREND[comparisonTrend]}</span>
        <ComparisonValue
          color={getComparisonTrendColor({
            value: comparisonValue,
            trendStyle,
            comparisonType,
          })}
        >
          {TYPES[comparisonType](comparisonValue)}
        </ComparisonValue>
        <span>{comparisonDescription}</span>
      </ComparisonContainer>
    </Container>
  )
}

export default BigNumber
